import { FeatureToggleNames } from '@src/composables/featureToggles/featureToggle.names'
import { computed, watch } from 'vue'
import { featureToggleList } from '@src/utilities/featureToggle'
import { useFeatureTogglesStore } from '@src/pinia/featureToggles'
import { storeToRefs } from 'pinia'
import { FeatureToggleVariants } from '@src/composables/featureToggles/featureToggle.variants'
import trackingServiceApi from '@src/api/tracking'
import useUser from '@src/composables/user'

export function useFeatureToggles() {
  const featureTogglesStore = useFeatureTogglesStore()

  const {
    featureToggles: releaseToggles,
    experiments,
    loading
  } = storeToRefs(featureTogglesStore)

  const llmConversationsEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_LLM_CONVERSATIONS).isEnabled())
  const installPwaEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_INSTALL_PWA).isEnabled())
  const searchSummaryEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_SEARCH_SUMMARY).isEnabled())
  const browserExtensionWidgetEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_BROWSER_EXTENSION_WIDGET).isEnabled())
  const connectionsDemoEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.CONNECTIONS_MAP_DEMO).isEnabled())
  const kiAdminNavEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_DEV_11385_KI_ADMIN_NAV).isEnabled())
  const llmConceptFilteringEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_DEV_10770_LLM_CONCEPT_FILTERING_MVP).isEnabled())
  const starmindAgentEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_STARDEV_11519_AGENT_ROLLOUT).isEnabled())
  const mcpInstallEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_STARDEV_11410_MCP_INSTALL).isEnabled())
  const pkceAuthEnabled = computed(() => featureToggleList(releaseToggles.value).findByName(FeatureToggleNames.RELEASE_NOJIRA_PKCE_AUTH).isEnabled())

  return {
    releaseToggles,
    searchSummaryEnabled,
    llmConversationsEnabled,
    browserExtensionWidgetEnabled,
    connectionsDemoEnabled,
    installPwaEnabled,
    kiAdminNavEnabled,
    llmConceptFilteringEnabled,
    starmindAgentEnabled,
    mcpInstallEnabled,
    pkceAuthEnabled,
    experiments,
    loading
  }
}

export function useExperiment<T extends string = FeatureToggleVariants>(name: FeatureToggleNames, automaticImpressionTracking = true) {
  const featureTogglesStore = useFeatureTogglesStore()
  const { user } = useUser()

  const {
    experiments,
    loading
  } = storeToRefs(featureTogglesStore)

  const variant = computed(() => featureToggleList(experiments.value).findByName(name).getExperimentVariant<T>())

  // Automatically tracks the impression wherever this experiment composable is used
  watch(loading, (value) => {
    if (!value && automaticImpressionTracking && variant.value) {
      trackingServiceApi.trackImpression(name, user.value.event_tracking_id, variant.value || '')
    }
  }, { immediate: true })

  return {
    variant,
    loading
  }
}
